import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getCustomFees, createCustomFee, updateCustomFee, deleteCustomFee } from "store/custom-fees"

export const useCustomFees = ({ enabled = false } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const customFees = useSelector((state) => state.customFees?.items || [])
  const customFeesLoading = useSelector((state) => state.customFees?.loading || false)
  const isCreating = useSelector((state) => state.customFees?.createCustomFee?.loading || false)
  const isUpdating = useSelector((state) => state.customFees?.updateCustomFee?.loading || false)
  const isDeleting = useSelector((state) => state.customFees?.deleteCustomFee?.loading || false)
  const isMutating = isCreating || isUpdating || isDeleting

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [feeToDelete, setFeeToDelete] = useState(null)
  const [feeToEdit, setFeeToEdit] = useState(null)

  useEffect(() => {
    if (enabled) dispatch(getCustomFees())
  }, [dispatch, enabled])

  const handleAddFee = async (values) => {
    try {
      const feeData = {
        title: values.title,
        price: Number.parseFloat(values.price),
        fee_type: values.fee_type
      }
      if (feeToEdit) {
        await dispatch(updateCustomFee(feeToEdit.id, feeData))
      } else {
        await dispatch(createCustomFee(feeData))
      }
      dispatch(getCustomFees())
      setIsModalOpen(false)
      setFeeToEdit(null)
    } catch (error) {
      console.error("Error saving custom fee:", error)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteCustomFee(feeToDelete.id))
      dispatch(getCustomFees())
      setDeleteModalOpen(false)
      setFeeToDelete(null)
    } catch (error) {
      console.error("Error deleting custom fee:", error)
    }
  }

  const getMenuItems = (record) => [
    {
      key: "edit",
      label: t("settings.action_edit"),
      onClick: () => {
        setFeeToEdit(record)
        setIsModalOpen(true)
      }
    },
    {
      key: "toggle",
      label: record.is_active ? t("settings.action_deactivate") : t("settings.action_activate"),
      onClick: async () => {
        try {
          await dispatch(updateCustomFee(record.id, { is_active: !record.is_active }))
          dispatch(getCustomFees())
        } catch (error) {
          console.error("Error toggling fee status:", error)
        }
      }
    },
    {
      key: "delete",
      label: t("settings.action_delete"),
      danger: true,
      onClick: () => {
        setFeeToDelete(record)
        setDeleteModalOpen(true)
      }
    }
  ]

  return {
    customFees,
    customFeesLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    deleteModalOpen,
    feeToEdit,
    handleAddFee,
    handleDeleteConfirm,
    handleDeleteCancel: () => {
      setDeleteModalOpen(false)
      setFeeToDelete(null)
    },
    openAddModal: () => {
      setFeeToEdit(null)
      setIsModalOpen(true)
    },
    closeModal: () => {
      setIsModalOpen(false)
      setFeeToEdit(null)
    },
    getMenuItems
  }
}
